/**
 * Account Stats Modal
 *
 * Displays account statistics for prop firm accounts including
 * drawdown tracking, P&L, trading days, and configuration.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useAccountStatsStore,
  AccountStatistics,
  AccountConfigUpdate,
} from '../store/accountStatsStore';
import './AccountStatsModal.css';

interface AccountStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'overview' | 'accounts' | 'settings';

export const AccountStatsModal: React.FC<AccountStatsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<AccountConfigUpdate | null>(null);

  // Use stable selectors to avoid infinite re-renders
  const accountsMap = useAccountStatsStore((state) => state.accounts);
  const accounts = useMemo(() => Array.from(accountsMap.values()), [accountsMap]);
  const summary = useAccountStatsStore((state) => state.summary);
  const firms = useAccountStatsStore((state) => state.firms);
  const isLoading = useAccountStatsStore((state) => state.isLoading);
  const requestAccounts = useAccountStatsStore((state) => state.requestAccounts);
  const updateConfig = useAccountStatsStore((state) => state.updateConfig);

  // Request data when modal opens
  useEffect(() => {
    if (isOpen) {
      requestAccounts();
    }
  }, [isOpen, requestAccounts]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const formatPercent = useCallback((value: number | undefined | null) => {
    if (value == null) return '0.0%';
    return `${value.toFixed(1)}%`;
  }, []);

  const getDrawdownPercent = useCallback((stats: AccountStatistics): number => {
    if (!stats.drawdownLimit || stats.drawdownLimit <= 0) return 0;
    return Math.min(100, (stats.currentDrawdown / stats.drawdownLimit) * 100);
  }, []);

  const handleSaveConfig = useCallback(
    (stats: AccountStatistics) => {
      if (!editingConfig) return;
      updateConfig(stats.brokerId, stats.accountId, editingConfig);
      setEditingConfig(null);
      setExpandedAccount(null);
    },
    [editingConfig, updateConfig]
  );

  if (!isOpen) return null;

  return (
    <div className="account-stats-modal-overlay" onClick={onClose}>
      <div
        className="account-stats-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="account-stats-modal-header">
          <div className="account-stats-modal-title">
            Account Statistics
          </div>
          <button className="account-stats-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Tabs */}
        <div className="account-stats-tabs">
          <button
            className={`account-stats-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`account-stats-tab ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            Accounts ({accounts.length})
          </button>
          <button
            className={`account-stats-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        {/* Content */}
        <div className="account-stats-modal-content">
          {isLoading ? (
            <div className="account-stats-loading">Loading...</div>
          ) : activeTab === 'overview' ? (
            <OverviewTab
              summary={summary}
              accounts={accounts}
              formatCurrency={formatCurrency}
              formatPercent={formatPercent}
            />
          ) : activeTab === 'accounts' ? (
            <AccountsTab
              accounts={accounts}
              expandedAccount={expandedAccount}
              setExpandedAccount={setExpandedAccount}
              editingConfig={editingConfig}
              setEditingConfig={setEditingConfig}
              handleSaveConfig={handleSaveConfig}
              formatCurrency={formatCurrency}
              formatPercent={formatPercent}
              getDrawdownPercent={getDrawdownPercent}
            />
          ) : (
            <SettingsTab firms={firms} />
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
interface OverviewTabProps {
  summary: import('../store/accountStatsStore').AccountStatsSummary | null;
  accounts: AccountStatistics[];
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  summary,
  accounts,
  formatCurrency,
  formatPercent,
}) => {
  if (!summary) {
    return (
      <div className="account-stats-empty">
        <div className="account-stats-empty-icon">📊</div>
        <div className="account-stats-empty-title">No Data Available</div>
        <div className="account-stats-empty-text">
          Connect to the account-stats service to see statistics.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="account-stats-summary">
        <div className="account-stats-summary-card">
          <div className="account-stats-summary-value">
            {summary.totalAccounts}
          </div>
          <div className="account-stats-summary-label">Total Accounts</div>
        </div>
        <div className="account-stats-summary-card">
          <div className="account-stats-summary-value">
            {summary.evalAccounts}
          </div>
          <div className="account-stats-summary-label">Eval Accounts</div>
        </div>
        <div className="account-stats-summary-card">
          <div className="account-stats-summary-value">
            {summary.fundedAccounts}
          </div>
          <div className="account-stats-summary-label">Funded Accounts</div>
        </div>
        <div className="account-stats-summary-card">
          <div
            className={`account-stats-summary-value ${
              summary.totalBalance >= 0 ? 'positive' : 'negative'
            }`}
          >
            {formatCurrency(summary.totalBalance)}
          </div>
          <div className="account-stats-summary-label">Total Balance</div>
        </div>
        <div className="account-stats-summary-card">
          <div
            className={`account-stats-summary-value ${
              summary.totalPnL >= 0 ? 'positive' : 'negative'
            }`}
          >
            {formatCurrency(summary.totalPnL)}
          </div>
          <div className="account-stats-summary-label">Total P&L</div>
        </div>
        <div className="account-stats-summary-card">
          <div className="account-stats-summary-value">
            {formatPercent(summary.avgDrawdownPercent)}
          </div>
          <div className="account-stats-summary-label">Avg Drawdown</div>
        </div>
      </div>

      {/* Quick account cards */}
      <h3 style={{ marginBottom: 16, color: 'var(--text-primary)' }}>
        Accounts by Firm
      </h3>
      <div className="account-stats-list">
        {accounts.slice(0, 5).map((stats) => (
          <QuickAccountCard
            key={`${stats.brokerId}:${stats.accountId}`}
            stats={stats}
            formatCurrency={formatCurrency}
          />
        ))}
      </div>
    </>
  );
};

// Quick Account Card for Overview
const QuickAccountCard: React.FC<{
  stats: AccountStatistics;
  formatCurrency: (v: number) => string;
}> = ({ stats, formatCurrency }) => (
  <div className="account-stats-card">
    <div className="account-stats-card-header">
      <div className="account-stats-card-info">
        <div className="account-stats-card-name">
          {stats.nickname || stats.accountName}
        </div>
        <div className="account-stats-card-firm">
          {stats.propFirm || 'Personal Account'}
        </div>
      </div>
      <div className="account-stats-card-badges">
        <span
          className={`account-stats-badge ${
            stats.customAccountType || stats.accountType
          }`}
        >
          {stats.customAccountType || stats.accountType}
        </span>
      </div>
    </div>
    <div className="account-stats-grid">
      <div className="account-stats-stat">
        <div
          className={`account-stats-stat-value ${
            stats.totalPnL >= 0 ? 'positive' : 'negative'
          }`}
        >
          {formatCurrency(stats.totalPnL)}
        </div>
        <div className="account-stats-stat-label">P&L</div>
      </div>
      <div className="account-stats-stat">
        <div className="account-stats-stat-value">
          {stats.daysTradedTotal}
        </div>
        <div className="account-stats-stat-label">Days Traded</div>
      </div>
      <div className="account-stats-stat">
        <div className="account-stats-stat-value">
          {formatCurrency(stats.currentBalance)}
        </div>
        <div className="account-stats-stat-label">Balance</div>
      </div>
    </div>
  </div>
);

// Accounts Tab
interface AccountsTabProps {
  accounts: AccountStatistics[];
  expandedAccount: string | null;
  setExpandedAccount: (key: string | null) => void;
  editingConfig: AccountConfigUpdate | null;
  setEditingConfig: (config: AccountConfigUpdate | null) => void;
  handleSaveConfig: (stats: AccountStatistics) => void;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
  getDrawdownPercent: (stats: AccountStatistics) => number;
}

const AccountsTab: React.FC<AccountsTabProps> = ({
  accounts,
  expandedAccount,
  setExpandedAccount,
  editingConfig,
  setEditingConfig,
  handleSaveConfig,
  formatCurrency,
  formatPercent,
  getDrawdownPercent,
}) => {
  if (accounts.length === 0) {
    return (
      <div className="account-stats-empty">
        <div className="account-stats-empty-icon">📊</div>
        <div className="account-stats-empty-title">No Accounts Found</div>
        <div className="account-stats-empty-text">
          Connect your broker accounts to start tracking statistics.
        </div>
      </div>
    );
  }

  return (
    <div className="account-stats-list">
      {accounts.map((stats) => {
        const key = `${stats.brokerId}:${stats.accountId}`;
        const isExpanded = expandedAccount === key;
        const drawdownPercent = getDrawdownPercent(stats);

        return (
          <div key={key} className="account-stats-card">
            <div className="account-stats-card-header">
              <div className="account-stats-card-info">
                <div className="account-stats-card-name">
                  {stats.nickname || stats.accountName}
                </div>
                <div className="account-stats-card-firm">
                  {stats.propFirm || 'Personal Account'} &bull; {stats.brokerId}
                </div>
              </div>
              <div className="account-stats-card-badges">
                <span
                  className={`account-stats-badge ${
                    stats.customAccountType || stats.accountType
                  }`}
                >
                  {stats.customAccountType || stats.accountType}
                </span>
                <span
                  className={`account-stats-badge ${
                    stats.isActive ? 'active' : 'inactive'
                  }`}
                >
                  {stats.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="account-stats-grid">
              <div className="account-stats-stat">
                <div
                  className={`account-stats-stat-value ${
                    stats.totalPnL >= 0 ? 'positive' : 'negative'
                  }`}
                >
                  {formatCurrency(stats.totalPnL)}
                </div>
                <div className="account-stats-stat-label">Total P&L</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  {formatCurrency(stats.currentBalance)}
                </div>
                <div className="account-stats-stat-label">Balance</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  {stats.daysTradedTotal}
                </div>
                <div className="account-stats-stat-label">Days Traded</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  {stats.daysTradedThisMonth}
                </div>
                <div className="account-stats-stat-label">This Month</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  {formatCurrency(stats.currentDrawdown)}
                </div>
                <div className="account-stats-stat-label">Drawdown</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  {formatCurrency(stats.highWaterMark)}
                </div>
                <div className="account-stats-stat-label">High Water</div>
              </div>
            </div>

            {/* Drawdown Bar */}
            {stats.drawdownLimit && stats.drawdownLimit > 0 && (
              <div className="account-stats-drawdown-bar">
                <div className="account-stats-drawdown-header">
                  <span className="account-stats-drawdown-label">
                    Drawdown Limit: {formatCurrency(stats.drawdownLimit)}
                  </span>
                  <span className="account-stats-drawdown-value">
                    {formatPercent(drawdownPercent)} used
                  </span>
                </div>
                <div className="account-stats-drawdown-track">
                  <div
                    className="account-stats-drawdown-fill"
                    style={{ width: `${drawdownPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Config Button */}
            <button
              className="account-stats-config-btn"
              onClick={() => {
                if (isExpanded) {
                  setExpandedAccount(null);
                  setEditingConfig(null);
                } else {
                  setExpandedAccount(key);
                  setEditingConfig({
                    drawdownLimit: stats.drawdownLimit,
                    profitTargetEval: stats.profitTargetEval,
                    profitTargetPayout: stats.profitTargetPayout,
                    customAccountType: stats.customAccountType,
                    startingBalance: stats.startingBalance,
                  });
                }
              }}
            >
              {isExpanded ? 'Close Config' : 'Configure'}
            </button>

            {/* Config Form */}
            {isExpanded && editingConfig && (
              <div className="account-stats-config">
                <div className="account-stats-config-row">
                  <label className="account-stats-config-label">
                    Account Type
                  </label>
                  <select
                    className="account-stats-config-select"
                    value={editingConfig.customAccountType || stats.accountType}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        customAccountType: e.target.value as 'eval' | 'funded' | 'personal',
                      })
                    }
                  >
                    <option value="eval">Evaluation</option>
                    <option value="funded">Funded</option>
                    <option value="personal">Personal</option>
                  </select>
                </div>
                <div className="account-stats-config-row">
                  <label className="account-stats-config-label">
                    Drawdown Limit ($)
                  </label>
                  <input
                    type="number"
                    className="account-stats-config-input"
                    value={editingConfig.drawdownLimit || ''}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        drawdownLimit: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    placeholder="e.g. 2500"
                  />
                </div>
                <div className="account-stats-config-row">
                  <label className="account-stats-config-label">
                    Profit Target - Eval ($)
                  </label>
                  <input
                    type="number"
                    className="account-stats-config-input"
                    value={editingConfig.profitTargetEval || ''}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        profitTargetEval: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    placeholder="e.g. 6000"
                  />
                </div>
                <div className="account-stats-config-row">
                  <label className="account-stats-config-label">
                    Profit Target - Payout ($)
                  </label>
                  <input
                    type="number"
                    className="account-stats-config-input"
                    value={editingConfig.profitTargetPayout || ''}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        profitTargetPayout: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    placeholder="e.g. 1000"
                  />
                </div>
                <div className="account-stats-config-row">
                  <label className="account-stats-config-label">
                    Starting Balance ($)
                  </label>
                  <input
                    type="number"
                    className="account-stats-config-input"
                    value={editingConfig.startingBalance || ''}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        startingBalance: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="e.g. 50000"
                  />
                </div>
                <div className="account-stats-config-actions">
                  <button
                    className="account-stats-config-save"
                    onClick={() => handleSaveConfig(stats)}
                  >
                    Save
                  </button>
                  <button
                    className="account-stats-config-cancel"
                    onClick={() => {
                      setExpandedAccount(null);
                      setEditingConfig(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Settings Tab
interface SettingsTabProps {
  firms: import('../store/accountStatsStore').PropFirmInfo[];
}

const SettingsTab: React.FC<SettingsTabProps> = ({ firms }) => {
  return (
    <>
      <h3 style={{ marginBottom: 16, color: 'var(--text-primary)' }}>
        Supported Prop Firms
      </h3>
      <div className="account-stats-list">
        {firms.map((firm) => (
          <div key={firm.id} className="account-stats-card">
            <div className="account-stats-card-header">
              <div className="account-stats-card-info">
                <div className="account-stats-card-name">{firm.name}</div>
                <div className="account-stats-card-firm">ID: {firm.id}</div>
              </div>
            </div>
            <div className="account-stats-grid">
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  ${firm.typicalDrawdownLimit?.toLocaleString() || '-'}
                </div>
                <div className="account-stats-stat-label">Typical Drawdown</div>
              </div>
              <div className="account-stats-stat">
                <div className="account-stats-stat-value">
                  ${firm.typicalProfitTarget?.toLocaleString() || '-'}
                </div>
                <div className="account-stats-stat-label">Typical Target</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AccountStatsModal;
